// MUI-free copy of Ui-components/Client-video-feedback for the plain homepage. Same markup,
// interactions and timings; the sx styles live in ClientVideoFeedback.module.scss and the
// MUI-Dialog is replaced by the local VideoDialog.
import Container from '@/components/atoms/Container';
import Eyebrow from '@/components/atoms/Eyebrow';
import Icon from '@/components/atoms/Icon';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import type {KeyboardEvent, MouseEvent} from 'react';
import VideoDialog from './VideoDialog';
import styles from './ClientVideoFeedback.module.scss';

const YouTubeEmbed = dynamic(() => import('@/components/Common/YouTubeEmbed'), {ssr: false, loading: () => null});

// Phones get a swipeable, self-advancing carousel rather than a tall static stack.
const MOBILE_AUTOPLAY_MS = 5000;

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

// Loop copies are decorative: hidden from assistive tech and unfocusable (`inert`; React 18
// has no typed prop for it, so it goes through as a plain attribute).
const cloneAttrs = {'aria-hidden': true, inert: ''} as Record<string, unknown>;

/** The original LinkedIn icon was a clickable SVG (no link): kept as a click target that opens the profile. */
const LinkedInButton = ({href, className, stopPropagation}: {href: string; className: string; stopPropagation?: boolean}) => {
    const openProfile = (e: MouseEvent | KeyboardEvent) => {
        if (stopPropagation) e.stopPropagation();
        globalThis.open(href, '_blank');
    };
    return (
        <span
            className={className}
            role="link"
            tabIndex={0}
            aria-label="LinkedIn profile"
            onClick={openProfile}
            onKeyDown={(e) => {
                if (e.key === 'Enter') openProfile(e);
            }}
        >
            <Icon name="LinkedIn" fontSize="inherit" />
        </span>
    );
};

const ClientAvatar = memo(({src, name}: {src: string; name: string}) => {
    const [err, setErr] = useState(false);
    const initials = name
        .split(' ')
        .slice(0, 2)
        .map((w: string) => w[0])
        .join('')
        .toUpperCase();

    if (!src || err) {
        return (
            <div className={styles.avatarFallback}>
                <p className={styles.avatarInitials}>{initials}</p>
            </div>
        );
    }

    return (
        <div className={styles.avatar}>
            <Image src={src} alt={name} fill style={{objectFit: 'cover'}} onError={() => setErr(true)} sizes="48px" />
        </div>
    );
});
ClientAvatar.displayName = 'ClientAvatar';

// ─── Video card ───────────────────────────────────────────────────────────────
const VideoCard = memo(({item, onPlay}: {item: any; onPlay: (url: string, src: string) => void}) => (
    <div className={styles.card} onClick={() => onPlay(item.videoLink, item.src)}>
        <div className={styles.imageWrapper}>
            <Image
                alt={item.name}
                src={item.src}
                fill
                style={{objectFit: 'cover', objectPosition: 'center top'}}
                sizes="(max-width:768px) 100vw, 50vw"
            />
            <div className={styles.playBtn}>
                <Icon name="PlayArrowRounded" fontSize="inherit" className={styles.playIcon} />
            </div>
        </div>
        <div className={styles.cardInfo}>
            <div className={styles.cardInfoRow}>
                <div>
                    <div className={styles.videoNameRow}>
                        <h3 className={cx('font-headers', styles.videoName)}>{item.name}</h3>
                        {item.linkedin && <LinkedInButton href={item.linkedin} className={styles.videoLinkedin} stopPropagation />}
                    </div>
                    <p className={cx('font-regular-text', styles.videoDesignation)}>{item.designation}</p>
                </div>
                {item.projectLink && (
                    <a
                        href={item.projectLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className={cx(styles.projectLink, styles.projectLinkDesktop, 'font-headers')}
                    >
                        Project Details &rarr;
                    </a>
                )}
            </div>
            {item.projectLink && (
                <a
                    href={item.projectLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={cx(styles.projectLink, styles.projectLinkMobile, 'font-headers')}
                >
                    Project Details &rarr;
                </a>
            )}
        </div>
    </div>
));
VideoCard.displayName = 'VideoCard';

// ─── Written review card ──────────────────────────────────────────────────────
const ReviewCard = memo(({item}: {item: any}) => (
    <div className={styles.reviewCard}>
        <div className={styles.reviewTop}>
            <Icon name="FormatQuote" fontSize="inherit" className={styles.reviewQuote} />
            <div className={styles.stars}>
                {[...Array(5)].map((_, i) => (
                    <Icon key={i} name="Star" fontSize="inherit" className={styles.star} />
                ))}
            </div>
        </div>

        <p className={cx('font-regular-text', styles.reviewText)}>{item.review}</p>

        <div className={styles.reviewFooter}>
            <div className={styles.reviewFooterRow}>
                <div className={styles.reviewPerson}>
                    <ClientAvatar src={item.src || ''} name={item.name} />
                    <div>
                        <div className={styles.reviewNameRow}>
                            <p className={cx('font-headers', styles.reviewName)}>{item.name}</p>
                            {item.linkedin && <LinkedInButton href={item.linkedin} className={styles.reviewLinkedin} />}
                        </div>
                        <p className={cx('font-regular-text', styles.reviewDesignation)}>{item.designation}</p>
                    </div>
                </div>
                {item.projectLink && (
                    <a href={item.projectLink} target="_blank" rel="noopener noreferrer" className={cx(styles.projectLink, 'font-headers')}>
                        Project Details &rarr;
                    </a>
                )}
            </div>
        </div>
    </div>
));
ReviewCard.displayName = 'ReviewCard';

// ─── Vertical auto-scrolling column ────────────────────────────────────────────
const MarqueeColumn = memo(
    ({items, direction, onPlay}: {items: any[]; direction: 'down' | 'up'; onPlay: (url: string, src: string) => void}) => {
        // The second copy only exists so the -50% keyframe loops seamlessly.
        const doubled = [...items, ...items];
        return (
            <div className={styles.marqueeViewport}>
                <div
                    className={cx('marquee-track', styles.marqueeTrack, direction === 'down' ? styles.scrollDown : styles.scrollUp)}
                    style={{animationDuration: `${items.length * 8}s`}}
                >
                    {doubled.map((item, i) => (
                        <div key={i} {...(i >= items.length ? cloneAttrs : {})}>
                            {item.isVideo ? <VideoCard item={item} onPlay={onPlay} /> : <ReviewCard item={item} />}
                        </div>
                    ))}
                </div>
            </div>
        );
    }
);
MarqueeColumn.displayName = 'MarqueeColumn';

// ─── Main component ───────────────────────────────────────────────────────────
const ClientVideo = (props: any) => {
    // `maxWidth` was accepted but ignored by MUIContainer; kept out of the destructure for the same result.
    const {heading, subheading, eyebrow, contentData} = props;

    const [open, setOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [thumbnailUrl, setThumbnailUrl] = useState('');

    const handleOpenVideo = useCallback((videoLink: string, src: string) => {
        setVideoUrl(videoLink);
        setThumbnailUrl(src);
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setVideoUrl('');
        setThumbnailUrl('');
    }, []);

    // Mobile carousel: the track scrolls, so the active index is read back from its scroll position.
    const trackRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    const scrollToSlide = useCallback((index: number) => {
        const track = trackRef.current;
        if (!track) return;
        track.scrollTo({left: index * track.clientWidth, behavior: 'smooth'});
    }, []);

    const handleTrackScroll = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;
        setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
    }, []);

    const data = contentData ?? [];

    // Autoplay, mobile only, and only while the track is on screen.
    useEffect(() => {
        const track = trackRef.current;
        if (!track || globalThis.window === undefined || globalThis.innerWidth >= 900 || data.length < 2) return undefined;

        let visible = false;
        const io = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), {threshold: 0.3});
        io.observe(track);

        const id = setInterval(() => {
            if (!visible || paused || document.hidden) return;
            const next = (Math.round(track.scrollLeft / track.clientWidth) + 1) % data.length;
            track.scrollTo({left: next * track.clientWidth, behavior: 'smooth'});
        }, MOBILE_AUTOPLAY_MS);

        return () => {
            clearInterval(id);
            io.disconnect();
        };
    }, [data.length, paused]);
    const columnSize = Math.ceil(data.length / 2) || 1;
    const columns = [data.slice(0, columnSize), data.slice(columnSize)].filter((col) => col.length > 0);
    const directions: Array<'down' | 'up'> = ['down', 'up'];

    return (
        <div>
            <Container>
                <div className={styles.layout}>
                    <div className={styles.introCol}>
                        {eyebrow && (typeof eyebrow === 'string' ? <Eyebrow text={eyebrow} /> : eyebrow)}
                        <h2 className={cx('font-headers', styles.introHeading)}>{heading}</h2>
                        {subheading && <p className={cx('font-regular-text', styles.introSubheading)}>{subheading}</p>}

                        <div className={styles.quoteStrip}>
                            <Icon name="FormatQuote" fontSize="inherit" className={styles.quoteIcon} />
                            <div className={styles.quoteBar} />
                            <p className={cx('font-regular-text', styles.quoteText)}>
                                Trusted by businesses across industries to turn complex ideas into successful digital products.
                            </p>
                        </div>
                    </div>

                    <div className={styles.gridCol}>
                        {/* Mobile/tablet: one card at a time, swipeable and self-advancing */}
                        <div
                            ref={trackRef}
                            className={styles.mobileTrack}
                            onScroll={handleTrackScroll}
                            onTouchStart={() => setPaused(true)}
                            onTouchEnd={() => setPaused(false)}
                        >
                            {data.map((item: any, i: number) => (
                                <div key={i}>
                                    {item.isVideo ? <VideoCard item={item} onPlay={handleOpenVideo} /> : <ReviewCard item={item} />}
                                </div>
                            ))}
                        </div>

                        <div className={styles.mobileDotsRow}>
                            {data.map((item: any, i: number) => (
                                <button
                                    key={i}
                                    type="button"
                                    aria-label={`Show review ${i + 1}`}
                                    aria-pressed={activeIndex === i}
                                    onClick={() => scrollToSlide(i)}
                                    className={cx(styles.mobileDot, activeIndex === i && styles.mobileDotActive)}
                                />
                            ))}
                        </div>

                        {/* Desktop: two parallel auto-scrolling columns, opposite direction */}
                        <div className={styles.columnsRow}>
                            {columns.map((col, i) => (
                                <MarqueeColumn key={i} items={col} direction={directions[i % directions.length]} onPlay={handleOpenVideo} />
                            ))}
                        </div>
                    </div>
                </div>
            </Container>

            <VideoDialog open={open} onClose={handleClose}>
                <span
                    className={styles.closeBtn}
                    role="button"
                    tabIndex={0}
                    aria-label="Close video"
                    onClick={handleClose}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') handleClose();
                    }}
                >
                    <Icon name="Close" fontSize="inherit" className="testmonial-video-close-icon" />
                </span>
                <YouTubeEmbed url={videoUrl} className="react-player-container" controls playing light={thumbnailUrl || true} />
            </VideoDialog>
        </div>
    );
};

export default memo(ClientVideo);
